import React, { useCallback, useState, useEffect } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { generateFullName } from 'utils/generateFullNameUtils';
import { Tooltip } from 'primereact/tooltip';

import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { LeadPrioritySortList } from 'constants/dropdownOptions';
import { sortByKey } from 'utils/utils';
import { LoaderComponent } from 'shared/LoaderComponent';
import { useFilters } from '../context/FilterContext';
import { useBoardData } from '../context/BoardContext';
import { handleFilterResetUserContext } from 'common/pagination';
import { useUserDetailsContext } from 'context/userDetailsContext';

export default function Filter() {
  const userContext = useUserDetailsContext();
  // Get filter state from FilterContext
  const { searchKeyword, practiceArea, assignedTo, sortType, setSearchKeyword, setPracticeArea, setAssignedTo, setSortType, clearFilters } =
    useFilters();

  // Get board data (practiceAreaList, userList, etc.)
  const { practiceAreaList, isPracticeAreaLoading, userList, isLoadingUsers } = useBoardData();

  // Local state for search input (for controlled input)
  const [localSearch, setLocalSearch] = useState(searchKeyword);

  // Sync local search with context
  useEffect(() => {
    setLocalSearch(searchKeyword);
  }, [searchKeyword]);

  // Debounced search handler
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchKeyword(localSearch);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [localSearch, setSearchKeyword]);

  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      const filterObj = {
        ...(userContext?.userDetails?.user_preferences?.filters?.leads || {}),
        [name === 'practice_area' ? 'filter_practice_area' : name === 'priority' ? 'sort_by_priority' : name]: value,
      };
      switch (name) {
        case 'keyword':
          setLocalSearch(value);
          break;
        case 'assigned_to':
          setAssignedTo(value);
          handleFilterResetUserContext(userContext, 'filters', 'leads', null, filterObj, true);
          break;
        case 'practice_area':
          setPracticeArea(value);
          handleFilterResetUserContext(userContext, 'filters', 'leads', null, filterObj, true);
          break;
        case 'priority':
          setSortType(value);
          handleFilterResetUserContext(userContext, 'filters', 'leads', null, filterObj, true);
          break;
        default:
          break;
      }
    },
    [setAssignedTo, setPracticeArea, setSortType]
  );

  const resetFilters = useCallback(() => {
    clearFilters();
    const resetFilterObj = {
      assigned_to: null,
      filter_practice_area: null,
      priority: null,
      practice_area: userContext?.userDetails?.user_preferences?.filters?.leads?.practice_area || [],
      show_unassigned_pa_column: userContext?.userDetails?.user_preferences?.filters?.leads?.show_unassigned_pa_column || false,
    };
    handleFilterResetUserContext(userContext, 'filters', 'leads', null, resetFilterObj, true);
  }, [clearFilters]);

  const assignToItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {generateFullName(option)}
      </>
    );
  };

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-start p-2 p-lg-0 filter-wrap lead-filter autowidth">
      <div className="p-2 autowidth">
        <span className="p-input-icon-left input-shadow autowidth">
          <i className="icon-search F-size16" />
          <TextSnippetWrapper
            type="text"
            placeholder="Search By Client"
            name="keyword"
            value={localSearch}
            onChange={handleFilterChange}
            className="p-inputtext p-component input-search"
          />
        </span>
      </div>
      <div className="d-flex align-items-center filter-wraper p-2">
        <span className="me-2 text-bold black-600  filter-label py-0">Filters: </span>
        <div className="d-flex flex-column me-2 lead-filter py-0">
          <MultiSelect
            name="assigned_to"
            value={assignedTo}
            onChange={handleFilterChange}
            options={sortByKey(userList, 'first_name') ?? []}
            optionLabel={(e) => generateFullName(e)}
            isSearchable
            placeholder="Select Assignee"
            className="w-100 input-shadow input-width input-height"
            optionValue="firm_user_id"
            filter
            filterPlaceholder="Search"
            resetFilterOnHide
            itemTemplate={assignToItemTemplate}
            panelFooterTemplate={isLoadingUsers ? LoaderComponent : null}
          />
        </div>
        <div className="d-flex flex-column me-2 lead-filter py-0">
          <Dropdown
            name="practice_area"
            value={practiceArea}
            placeholder="Select Practice Area"
            options={practiceAreaList?.filter((item) => item.status === 'ACTIVE') ?? []}
            filter
            filterPlaceholder="Search"
            optionLabel="practice_area_name"
            optionValue="practice_area_id"
            className="w-100 input-shadow input-width input-height"
            onChange={handleFilterChange}
            resetFilterOnHide
            panelFooterTemplate={isPracticeAreaLoading ? LoaderComponent : null}
          />
        </div>
        <div className="d-flex flex-column me-2 lead-filter py-0">
          <Dropdown
            name="priority"
            value={sortType}
            placeholder="Sort by Priority"
            options={LeadPrioritySortList}
            optionLabel="label"
            optionValue="value"
            className="w-100 input-shadow input-width input-height"
            onChange={handleFilterChange}
            resetFilterOnHide
          />
        </div>
        <div className="d-flex pointer align-items-center p-2">
          <i className="fas fa-sync-alt" onClick={resetFilters} />
          <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
        </div>
      </div>
    </div>
  );
}
